import type { CheckboxProps } from '@latitude/checkbox';
import { Checkbox } from '@latitude/checkbox';
import { Stack } from '@latitude/layout';
import { FormComponentWrapper } from 'components/FormComponentWrapper';
import { useMemo } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { FormCheckboxGroupOptions, FormCheckboxGroupProps } from './types';

export const FormCheckboxGroup = <
  OptionType extends { value: string } = FormCheckboxGroupOptions,
  T extends FieldValues = FieldValues,
>({
  name,
  control,
  options,
  description,
  label,
  showSelectAll,
  wrapperClassname,
  disabled,
  rules,
  render,
  hideError,
  hideCheckbox,
}: FormCheckboxGroupProps<OptionType, T> & { children?: React.ReactNode }) => {
  const allValues = useMemo(() => options.map((opt) => opt.value), [options]);
  return (
    <Controller
      name={name}
      {...(control
        ? { control }
        : {
            // if undefined, Controller reads from FormProvider
          })}
      rules={rules}
      render={({
        field: { onBlur, onChange, value },
        fieldState: { error },
      }) => {
        const valueArray: string[] = Array.isArray(value) ? value : [];
        const isIndeterminate =
          valueArray.length > 0 && allValues.length > valueArray.length;

        // oxlint-disable-next-line no-explicit-any
        const handleOnChange: typeof onChange = (...event: any[]) => {
          onChange(...event);
          onBlur();
        };

        const displayError = hideError ? undefined : error;
        return (
          <FormComponentWrapper
            name={name}
            description={description}
            label={label}
            error={displayError}
            className={wrapperClassname}
            disabled={disabled}
          >
            <Stack gap={8}>
              {showSelectAll ? (
                <Checkbox
                  disabled={disabled}
                  invalid={!!displayError}
                  label="Select all"
                  indeterminate={isIndeterminate}
                  checked={valueArray.length === allValues.length}
                  onBlur={onBlur}
                  data-testid={`select-all-${name}`}
                  hideCheckbox={hideCheckbox}
                  onCheckedChange={(state: boolean) => {
                    if (!state || isIndeterminate) {
                      handleOnChange([]);
                      return;
                    }
                    handleOnChange(allValues);
                  }}
                />
              ) : null}
              {options.map((option, index) => {
                const { value, ...restProps } = option;

                const checkboxProps: Omit<CheckboxProps, 'onBlur'> = {
                  disabled,
                  invalid: !!displayError,
                  ...('label' in option && { label: option.label as string }),
                  checked: valueArray.includes(value),
                  'data-testid': `checkbox-${name}-${index}`,
                  onCheckedChange: (state: boolean) => {
                    if (state) {
                      handleOnChange([...valueArray, value]);
                    } else {
                      handleOnChange(valueArray.filter((val) => val !== value));
                    }
                  },
                  ...restProps,
                };
                return render ? (
                  <div className="inline-block" key={`${value}-${index}`}>
                    {render(option, checkboxProps)}
                  </div>
                ) : (
                  <Checkbox key={`${value}-${index}`} {...checkboxProps} />
                );
              })}
            </Stack>
          </FormComponentWrapper>
        );
      }}
    />
  );
};
